import { flash, SubmitButton } from "@xola/ui-kit";
import { SwitchField } from "common";
import React, { ReactNode } from "react";
import { useMutation } from "react-query";
import { GiftPreference, UserDetailsRequirement } from "types";
import { api } from "@/api/api";
import { SecondaryButton } from "@/components/Common/SecondaryButton";
import { XolaFormik } from "@/components/Common/XolaFormik";
import { ContentLayout } from "@/components/Layout/ContentLayout";
import { UserDetailsRequirementSelector } from "@/components/Product/GuestManagement/PurchaserInformationSection";
import { useSeller } from "@/hooks/useAccount";
import { useInvalidate } from "@/hooks/useInvalidation";
import { DEFAULT_GIFT_PREFERENCE, getGiftDetailsRequirements } from "@/models/GiftPreference";

export const GiftGlobalSettings = () => {
    const { id: sellerId, preferences } = useSeller();
    const invalidate = useInvalidate();
    const initialValues: GiftPreference = {
        ...DEFAULT_GIFT_PREFERENCE,
        ...preferences.gift,
        ...getGiftDetailsRequirements(preferences.gift),
    };

    const { mutateAsync: updateGiftPreference } = useMutation(async (values: GiftPreference) => {
        await api.post(`/sellers/${sellerId}/preferences/gift`, values);
        invalidate("sellers", sellerId);
        flash.success("Successfully saved Gift settings");
    });

    const handleSubmit = async (values: GiftPreference) => {
        await updateGiftPreference(values);
    };

    return (
        <ContentLayout isWide>
            <XolaFormik enableReinitialize initialValues={initialValues} onSubmit={handleSubmit}>
                {({ values, setFieldValue, submitForm, handleReset, isSubmitting }) => (
                    <>
                        <div className="space-y-6 py-12">
                            <h3>Global Settings</h3>
                            <SettingItem label="Recipient Details">
                                <UserDetailsRequirementSelector
                                    label="Recipient name and email are"
                                    value={values.recipientDetailsRequirement ?? UserDetailsRequirement.REQUIRED}
                                    testId="gift-settings-recipient-requirement"
                                    onChange={(value) => setFieldValue("recipientDetailsRequirement", value)}
                                />
                            </SettingItem>

                            <SettingItem label="Organizer Details">
                                <UserDetailsRequirementSelector
                                    label="Organizer details are"
                                    value={values.organizerDetailsRequirement ?? UserDetailsRequirement.REQUIRED}
                                    testId="gift-settings-organizer-requirement"
                                    onChange={(value) => setFieldValue("organizerDetailsRequirement", value)}
                                />
                            </SettingItem>

                            <SettingItem label="Organizer Phone Number Required">
                                <SwitchField
                                    className="!items-start"
                                    isChecked={values.customerPhoneRequired}
                                    onChange={(value) => setFieldValue("customerPhoneRequired", value)}
                                >
                                    Require gift purchasers to provide a phone number to checkout
                                </SwitchField>
                            </SettingItem>

                            <SettingItem label="Show Additional Experiences in Gift Email">
                                <SwitchField
                                    className="!items-start"
                                    isChecked={values.showAdditionalExperiences}
                                    onChange={(value) => setFieldValue("showAdditionalExperiences", value)}
                                >
                                    Show additional bookable listings in the Gift Certificate email sent to my customers
                                </SwitchField>
                            </SettingItem>
                        </div>

                        <ContentLayout.Footer>
                            <SubmitButton isLoading={isSubmitting} onClick={submitForm}>
                                Save
                            </SubmitButton>
                            <SecondaryButton onClick={handleReset}>Cancel</SecondaryButton>
                        </ContentLayout.Footer>
                    </>
                )}
            </XolaFormik>
        </ContentLayout>
    );
};

interface SettingItemProps {
    label: string;
    children: ReactNode;
}

const SettingItem = ({ label, children }: SettingItemProps) => (
    <div className="space-y-1">
        <div className="text-base font-bold">{label}</div>
        {children}
    </div>
);
